import React, { useEffect, useState, useContext } from 'react';
import {
  CallLogTableDataWeb,
  ModalCallRecording,
  ModalCallSummary,
  Searchbar,
} from '../../features';
import { CommonFilter } from '../../features/Jobs/commonFilter';
import {
  useFunctionalityBookingSummary,
  useFunctionalityCallLog,
  useFunctionalityCallSummary,
  useFunctionalityColumnSelections,
  useFunctionalityUserProfile,
  useFunctionalityUserGetDetailsById,
} from '../hooks';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import {
  CallLogPagedResponseModel,
  CallSummaryResponseModel,
} from '../../models/api/Call';
import {
  AppConstants,
  AppRouteUi,
  CallLogType,
  CallStatusType,
  ColumnSelectionTableType,
  ExportTableType,
  PaginationSettings,
  topLoaderProgress,
} from '../../configs';
import LoadingBar from 'react-top-loading-bar';
import { ActionAuth, useAppSelector, VRIAppStateType, useAppDispatch, AudioDeviceContext } from '../../core';
import { useFunctionalityCallLogColumns } from '../hooks/functionalityCallLogColumns';
import { CallLogInfoBox } from '../../features/Common/Components/CallLogInfoBox';
import { ApiSchemaMenuSettingsData } from '../../models/api/menuSettings';
import ReactDOM from 'react-dom';
import { ColumnSelectionsModel } from '../../models';
import { ApiSchemaFilter } from '../../models/api/filter';
import useFunctionalityMediaPermissions from '../hooks/mediaPermission/functionalityMediaPermission';
import { controlBrowserBackAndReloadButton } from '../../helpers';
import { isElectron } from '../../utils';

export function PageOperatorCallLog(): JSX.Element {
  const { operatorUserId } = useParams<{ operatorUserId: string }>();
  const { userDetailsData } = useFunctionalityUserGetDetailsById({ userId: operatorUserId });
  const operatorName = userDetailsData?.id
    ? (userDetailsData.name?.trim() || [userDetailsData.firstName, userDetailsData.lastName].filter(Boolean).join(' ').trim())
    : '';
  return <PageCallLog key={operatorUserId} operatorUserId={operatorUserId} operatorName={operatorName} />;
}

export function PageCallLog({ operatorUserId, operatorName }: { operatorUserId?: string; operatorName?: string } = {}): JSX.Element {
  const [loaderProgress, setLoaderProgress] = React.useState(0);
  const [show, setShow] = React.useState(false);
  const [recordingModalShow, setRecordingModalShow] = React.useState(false);
  const [recordingCallId, setRecordingCallId] = React.useState<string>("");
  const [isShowCommonFilter, setIsShowCommonFilter] = useState<boolean>(false);
  const [callSummaryData, setCallSummaryData] =
    React.useState<CallSummaryResponseModel>(Object);
  const [pageIndex, setPageIndex] = useState(
    PaginationSettings.initialPageIndex
  );
  const currentSession = useAppSelector((state: VRIAppStateType) => ({
    profile: state.auth.profile,
    userRole: state.auth.profile?.userRole,
    hasSeenModal: state.auth.profile?.hasSeenModal,
    callStatus: state.videoCall.CallStatus,
  }));
  const { controlBrowserBackButton } = controlBrowserBackAndReloadButton();
  const [pageSize, setPageSize] = useState(PaginationSettings.initialPageSize);
  const [searchText, setSearchText] = useState<string>('');
  const [callLogData, setCallLogData] =
    useState<CallLogPagedResponseModel>(Object);
  const [pagedCallListCount, setPagedCallListCount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [promptedThisSession, setPromptedThisSession] = useState(false);
  const history = useHistory();
  const location = useLocation<{
    callId: string;
    bookingId: string;
    CallStatus: CallStatusType;
  }>();
  const callId = location.state?.callId;
  const bookingId = location.state?.bookingId;
  const { LoadById } = useFunctionalityCallSummary();
  const { LoadBookingSummaryById } = useFunctionalityBookingSummary();
  const [menuSettings, setMenuSettings] = useState<ApiSchemaMenuSettingsData>();
  const { stopBothVideoAndAudio } = useFunctionalityMediaPermissions();
  const dispatch = useAppDispatch();
  const { resumeAudioAndRegister } = useContext(AudioDeviceContext);



  const getCallLogType = () => {
    if (operatorUserId) return CallLogType.CallLog;
    if (
      currentSession.userRole === AppConstants.UserRoles.Operator ||
      currentSession.userRole === AppConstants.UserRoles.User ||
      currentSession.userRole === AppConstants.UserRoles.CompanyAdmin ||
      currentSession.userRole === AppConstants.UserRoles.CompanyFinance
    )
      return CallLogType.CallLog;
    else return CallLogType.InternalCall;
  };

  const [columnSelection, setColumnSelection] = useState<
    ColumnSelectionsModel[]
  >([]);
  const [columnSelectionModalShow, setColumnSelectionModalShow] =
    React.useState(false);
  const isAdminUser = currentSession.userRole === AppConstants.UserRoles.Admin;
  const columnSelectionForModal = isAdminUser
    ? columnSelection
    : columnSelection.filter(
        (eachColumn) => eachColumn.propertyName.toLowerCase() !== 'sid'
      );
  const getColumnSelectionsType = () => {
    if (operatorUserId) return ColumnSelectionTableType.CallLog;
    if (
      currentSession.userRole === AppConstants.UserRoles.Operator ||
      currentSession.userRole === AppConstants.UserRoles.User ||
      currentSession.userRole === AppConstants.UserRoles.CompanyAdmin ||
      currentSession.userRole === AppConstants.UserRoles.CompanyFinance
    )
      return ColumnSelectionTableType.CallLog;
    else return ColumnSelectionTableType.InternalCall;
  };
  const { LoadColumnSelections } = useFunctionalityColumnSelections({
    type: getColumnSelectionsType(),
  });

  useEffect(() => {
    LoadColumnSelections().then((r) => {
      if (r?.data) setColumnSelection(r?.data);
    });
  }, []);

  useEffect(() => {
    stopBothVideoAndAudio();
  }, [callSummaryData]);

  const { callLogColumn } = useFunctionalityCallLogColumns({
    userRole: currentSession.userRole ?? '',
    setShow: setShow,
    setCallSummaryData: setCallSummaryData,
    setLoaderProgress: setLoaderProgress,
    callLogType: getCallLogType(),
    columnSelection: columnSelection,
    onOpenRecording: (row) => {
      const mediaRow = row as any;
      setRecordingCallId(mediaRow.id ?? "");
      setRecordingModalShow(true);
    },
  });
  const handleRecordingModalShow = (value: boolean) => {
    setRecordingModalShow(value);
    if (!value) {
      setRecordingCallId("");
    }
  };

  const handleReadyClick = async () => {
    if (currentSession.profile) {
      const updatedProfile = { ...currentSession.profile, hasSeenModal: true };
      dispatch(ActionAuth.SetProfileData(updatedProfile));
    }

    await resumeAudioAndRegister();
    setShowModal(false);
  };

  let initialFilterData: ApiSchemaFilter = {
    filterQueries: [],
    sortQuery: {},
  };
  const [filterData, setFilterData] =
    useState<ApiSchemaFilter>(initialFilterData);
  const [filterDataChange, setFilterDataChange] = useState<number>(0);

  const { LoadCallLog } = useFunctionalityCallLog({
    operatorUserId,
    pageSize: pageSize,
    pageNo: pageIndex,
    searchText: searchText?.trimStart(),
    callLogType: getCallLogType(),
    setLoaderProgress,
    filterData,
  });

  useEffect(() => {
    if (!callId && !bookingId) return;

    setLoaderProgress(topLoaderProgress.start);
    const loadSummary = async () => {
      try {
        if (callId) {
          const data = await LoadById(callId);
          if (data?.id) {
            setCallSummaryData(data);
            setShow(true);
          }
        } else if (bookingId) {
          const data = await LoadBookingSummaryById(bookingId);
          if (data) {
            setCallSummaryData(data);
            setShow(true);
          }
        }
      } finally {
        setLoaderProgress(topLoaderProgress.complete);
        history.replace({
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
          state: undefined,
        });
      }
    };

    loadSummary();
  }, [callId, bookingId, history, location.hash, location.pathname, location.search]);

  useEffect(() => {
    setLoaderProgress(topLoaderProgress.start);
    LoadCallLog().then((r) => {
      setLoaderProgress(topLoaderProgress.complete);

      if (r?.data) {
        setCallLogData(r?.data);
        setPagedCallListCount(r?.data.totalCount);
      }
    });
  }, [pageSize, pageIndex, filterData, searchText, operatorUserId]);

  useEffect(() => { }, [menuSettings]);
  const { getMenusettings } = useFunctionalityUserProfile();
  useEffect(() => {
    if (currentSession.userRole == AppConstants.UserRoles.Operator) {
      setLoaderProgress(topLoaderProgress.start);
      getMenusettings().then((d) => {
        if (d) setMenuSettings(d);
        setLoaderProgress(topLoaderProgress.complete);
      });
    }
  }, []);

  const onChangePageIndex = (index: number) => {
    setPageIndex(index);
  };
  const onChangePageSize = (size: number) => {
    setPageSize(size);
  };
  const handleSearch = (e: any) => {
    ReactDOM.unstable_batchedUpdates(() => {
      setSearchText(e.target.value);
      setPageIndex(1);
    });
  };

  const getExportTableType = () => {
    if (operatorUserId) return ExportTableType.CallLog;
    if (
      currentSession.userRole === AppConstants.UserRoles.Operator ||
      currentSession.userRole === AppConstants.UserRoles.User ||
      currentSession.userRole === AppConstants.UserRoles.CompanyAdmin ||
      currentSession.userRole === AppConstants.UserRoles.CompanyFinance
    )
      return ExportTableType.CallLog;
    else return ExportTableType.InternalCall;
  };

  useEffect(() => {
    if (
      location.state?.CallStatus === CallStatusType.Canceled ||
      currentSession.callStatus?.current === CallStatusType.Rejected
    ) {
      controlBrowserBackButton();
    }
  }, [location.state?.CallStatus, currentSession.callStatus]);


  useEffect(() => {
    if (
      !isElectron() &&
      currentSession.userRole === AppConstants.UserRoles.Operator &&
      currentSession.profile &&
      !currentSession.profile.hasSeenModal &&
      !promptedThisSession
    ) {
      setShowModal(true);
      setPromptedThisSession(true);
    }
  }, [currentSession.profile, currentSession.userRole, promptedThisSession]);

  return (
    <>
      {!isElectron() && currentSession.userRole === AppConstants.UserRoles.Operator && showModal &&
        <div className="user-agent-click-overlay">
          <div className="ready-modal">
            <div className="ready-card shadow">
              <div className="ready-badge">⚡</div>
              <h5 className="mb-2">Ready to receive calls?</h5>
              <p className="text-muted mb-4">Click below to ensure browser readiness.</p>
              <button className="btn btn-primary px-4" onClick={handleReadyClick}>I&apos;m Ready</button>
            </div>
          </div>
        </div>
      }
      <LoadingBar
        progress={loaderProgress}
        onLoaderFinished={() => setLoaderProgress(0)}
      />
      <div className="d-flex align-items-center justify-content-between flex-wrap my-3">
        <h4 className="mb-0 font-weight-light">
          {operatorUserId ? (
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb bg-transparent p-0">
                <li className="breadcrumb-item">
                  <Link to={AppRouteUi.Operator.Root} className="font-weight-light">
                    Operators
                  </Link>
                </li>
                <li className="breadcrumb-item font-weight-light active" aria-current="page">
                  Operator Call Log
                  {operatorName && (
                    <span className="operator-call-log-name-pill ml-2">{operatorName}</span>
                  )}
                </li>
              </ol>
            </nav>
          ) : 'Internal Call'}
        </h4>
        {operatorUserId && (
          <Link to={AppRouteUi.Operator.Root} className="btn btn-light btn-sm back-to-operators-btn ml-auto">
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" aria-hidden="true" />
            Back to operators
          </Link>
        )}
      </div>
      <div className="call-log-page ">
        <div className="row">
          <div className="col-12">
            <Searchbar
              showFilter={true}
              showExportOptions={true}
              showExcelOption={true}
              showPdfOption={true}
              showColumnSelectionOption={true}
              filterPanel={'commonFilterPanel'}
              searchText={searchText}
              handleSearch={handleSearch}
              columnSelectionModalShow={columnSelectionModalShow}
              setColumnSelectionModalShow={setColumnSelectionModalShow}
              columnSelection={columnSelectionForModal}
              setColumnSelection={setColumnSelection}
              showAutoRefresh={true}
              tableType={getExportTableType()}
              operatorUserId={operatorUserId}
              filterData={filterData}
              downloadedFileName={'CallLog'}
              setLoaderProgress={setLoaderProgress}
              setIsShowCommonFilter={setIsShowCommonFilter}
            />
          </div>
          {isShowCommonFilter ? (
            <CommonFilter
              setFilterData={setFilterData}
              setLoaderProgress={setLoaderProgress}
              setFilterDataChange={setFilterDataChange}
              pageName="callLogPage"
              callLogVariant={operatorUserId ? 'default' : 'internal'}
              hideOperatorFilter={Boolean(operatorUserId)}
              setPageIndex={setPageIndex}
            />
          ) : null}

          {!operatorUserId && (
            <CallLogInfoBox
              callLogData={callLogData}
              userRole={currentSession.userRole ?? ''}
            />
          )}
          <CallLogTableDataWeb
            className={operatorUserId ? 'mt-3' : undefined}
            pagingKey={operatorUserId ? undefined : 'PageCallLog'}
            loaderProgress={loaderProgress}
            pageHeader="Call Log"
            isPageEnabled={true}
            tableData={callLogData.callLogs}
            tableHeaders={callLogColumn}
            pageIndex={pageIndex}
            pageSize={pageSize}
            count={pagedCallListCount}
            searchText={searchText}
            filterDataChange={filterDataChange}
            onChangePageIndex={onChangePageIndex}
            onChangePageSize={onChangePageSize}
            setCallSummaryData={setCallSummaryData}
            setLoaderProgress={setLoaderProgress}
            setShow={setShow}
          />
          <ModalCallSummary
            showAdminViewBadge={Boolean(operatorUserId) && isAdminUser}
            summary={callSummaryData}
            show={show}
            handleShow={setShow}
            onRefreshSummary={setCallSummaryData}
          />
          <ModalCallRecording
            show={recordingModalShow}
            handleShow={handleRecordingModalShow}
            callId={recordingCallId}
          />
        </div>
      </div>
    </>
  );
}
